import { useMemo, useState } from 'react';
import {
    ArrowRight, Banknote, CreditCard, MessageSquare, Eye, Package, ChefHat, Undo2, Loader2,
} from 'lucide-react';
import { AKCENT, OK, CHYBA, POZOR, kc, hm, tint, ocekavanaPlatba } from '../RozvozCockpit/ui';
import { skloMaterial } from '../../components/ui/Sklo';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Karta rozvozové dávky v Moje směně (návrh „Karta dávky v Moje směně",
 * 2026-10-12). Odpovídá na tři otázky — kam teď, kolik vybrat, kdy zpátky —
 * a má jednu velkou akci, která vrátí rozvozce do cockpitu.
 *
 * Kostra je ve všech stavech stejná: stav nahoře, jedna velká informace,
 * souhrn a jedna hlavní akce.
 *  - `v_priprave` — kuchyň dávku ještě vaří (průběh),
 *  - `pripraveno` / `potvrzena` / `predano_ridici` — vyzvednout v bistru,
 *  - `na_ceste` — adresa, kam se jede, platba a stav SMS,
 *  - `uzavreno` — jak jízda dopadla a co vrátit do bistra. Hotovost se tu
 *    neodevzdává, to patří k vyúčtování směny.
 *
 * Seznam zastávek na kartě není — celou trasu má cockpit, karta tak zůstane
 * stejně vysoká při jedné i šesti zastávkách. Barva je barva sekce rozvozu
 * (oranžová), plocha neutrální grafit z tokenů.
 */
export default function DavkaPanel({ davka, zastavky = [], nadchazejici = [], onRefetch, onOpenCockpit }) {
    // Data chodí zvenku (`PracovniPlocha`) — rodič podle dávky dělí plochu.
    if (!davka) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0, margin: '6px 12px 4px' }}>
            <Karta davka={davka} zastavky={zastavky} onOpenCockpit={onOpenCockpit} onRefetch={onRefetch} />
            {nadchazejici.map(nd => (
                <DalsiDavka key={nd.davka.id} davka={nd.davka} zastavky={nd.zastavky} onOpenCockpit={onOpenCockpit} />
            ))}
        </div>
    );
}

const ceka = (z) => (z.stav_doruceni ?? 'ceka') === 'ceka';
const zaplaceno = (z) => ['online_zaplaceno', 'zaplaceno_hotove', 'zaplaceno_kartou'].includes(z.stav_platby);
const ulice = (adresa) => String(adresa || '').split(',')[0] || '—';
const zastavek = (n) => (n === 1 ? 'zastávka' : n < 5 ? 'zastávky' : 'zastávek');

/** Kolik zbývá vybrat — hotově a kartou zvlášť, online zaplacené ne. */
function kVybrani(zastavky) {
    let hotove = 0, kartou = 0;
    for (const z of zastavky) {
        if (!ceka(z) || zaplaceno(z)) continue;
        if (ocekavanaPlatba(z) === 'kartou') kartou += z.castka_kc || 0; else hotove += z.castka_kc || 0;
    }
    return { hotove, kartou, celkem: hotove + kartou };
}

/** Barva času u zákazníka podle slibu: včas / do 5 min / víc. */
function barvaCasu(rozdilSec) {
    if (rozdilSec == null || rozdilSec <= 0) return 'var(--text-primary)';
    return rozdilSec <= 300 ? POZOR : CHYBA;
}

function Karta({ davka, zastavky, onOpenCockpit, onRefetch }) {
    const stav = davka.stav;
    if (stav === 'uzavreno') return <KartaNavrat davka={davka} zastavky={zastavky} onRefetch={onRefetch} />;
    if (stav === 'v_priprave') return <KartaKuchyn davka={davka} zastavky={zastavky} />;
    if (stav === 'na_ceste') return <KartaNaCeste davka={davka} zastavky={zastavky} onOpenCockpit={onOpenCockpit} />;
    return <KartaVyzvednout davka={davka} zastavky={zastavky} onOpenCockpit={onOpenCockpit} />;
}

function KartaNaCeste({ davka, zastavky, onOpenCockpit }) {
    const idx = useMemo(() => {
        const i = zastavky.findIndex(ceka);
        return i === -1 ? zastavky.length : i;
    }, [zastavky]);
    const z = zastavky[idx] ?? null;
    const penize = kVybrani(zastavky);
    const platiKartou = z && ocekavanaPlatba(z) === 'kartou';

    return (
        <Deska>
            <Hlava barva={AKCENT} stav={z ? 'Na cestě' : 'Cesta zpět'} kod={davka.kod} pulz />
            <Prubeh zastavky={zastavky} idx={idx} />
            {z ? (
                <>
                    <Hlavni
                        nad={`Teď jedeš · ${idx + 1}. zastávka z ${zastavky.length}`}
                        velke={ulice(z.adresa)}
                        pod={[z.jmeno, `#${z.cislo_denni ?? z.cislo_objednavky}`].filter(Boolean).join(' · ')}
                    />
                    <Radek>
                        {zaplaceno(z)
                            ? <Stitek barva={OK}>zaplaceno online</Stitek>
                            : <Stitek barva={AKCENT} Icon={platiKartou ? CreditCard : Banknote}>{platiKartou ? 'platí kartou' : 'platí hotově'}</Stitek>}
                        {z.sms_blizko && <Stitek barva={OK} Icon={MessageSquare}>SMS odeslána</Stitek>}
                        {z.zakaznik_vidi && <Stitek barva={OK} Icon={Eye}>Vidí tě v mapě</Stitek>}
                    </Radek>
                    <Souhrn polozky={[
                        { hodnota: hm(z.cas_predpoklad), popis: z.cas_slibeny ? `u zákazníka · slíbeno ${String(z.cas_slibeny).slice(0, 5)}` : 'u zákazníka', barva: barvaCasu(z.rozdil_sec) },
                        { hodnota: hm(davka.cas_navratu_predpoklad), popis: 'zpět v bistru' },
                        { hodnota: kc(penize.celkem), popis: 'zbývá vybrat' },
                    ]} />
                </>
            ) : (
                <Hlavni nad="Všechny zastávky vyřízené" velke="Jeď do bistra" pod={`návrat ${hm(davka.cas_navratu_predpoklad)}`} />
            )}
            <Akce barva={AKCENT} onClick={onOpenCockpit}>Pokračovat v rozvozu</Akce>
        </Deska>
    );
}

function KartaVyzvednout({ davka, zastavky, onOpenCockpit }) {
    const penize = kVybrani(zastavky);
    const krabic = zastavky.reduce((s, z) => s + (z.krabic || 0), 0);
    const prvni = zastavky[0];
    // Jen názvy ulic — trasa se má přečíst na jeden pohled.
    const trasa = zastavky.map(z => ulice(z.adresa).replace(/\s+\d.*$/, '')).join(' → ');

    return (
        <Deska>
            <Hlava barva={AKCENT} stav="Vyzvedni v bistru" kod={davka.kod} pulz />
            <Hlavni
                nad={`Trasa · ${zastavky.length} ${zastavek(zastavky.length)}`}
                velke={trasa || '—'}
                pod={prvni?.cas_predpoklad
                    ? `první u zákazníka v ${hm(prvni.cas_predpoklad)}${prvni.cas_slibeny ? ` · slíbeno ${String(prvni.cas_slibeny).slice(0, 5)}` : ''}`
                    : null}
                mensi
            />
            <Radek>
                {krabic > 0 && <Stitek Icon={Package}>{krabic}×</Stitek>}
                {penize.hotove > 0 && <Stitek barva={AKCENT} Icon={Banknote}>hotově {kc(penize.hotove)}</Stitek>}
                {penize.kartou > 0 && <Stitek Icon={CreditCard}>kartou {kc(penize.kartou)}</Stitek>}
                {penize.celkem === 0 && <Stitek barva={OK}>vše zaplaceno online</Stitek>}
            </Radek>
            <Akce barva={AKCENT} onClick={onOpenCockpit}>Vyzvednout a jet</Akce>
        </Deska>
    );
}

function KartaKuchyn({ davka, zastavky }) {
    const celkem = davka.kds_polozek_celkem ?? 0;
    const hotovo = davka.kds_polozek_hotovych ?? 0;
    const pct = celkem > 0 ? Math.round((hotovo / celkem) * 100) : 0;
    const penize = kVybrani(zastavky);

    return (
        <Deska>
            <Hlava barva="var(--text-secondary)" stav="Kuchyň ještě vaří" kod={davka.kod} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Nad>Hotovo {hotovo} z {celkem} položek</Nad>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--surface-elevated)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: AKCENT, borderRadius: 4, transition: 'width 400ms ease-out' }} />
                </div>
            </div>
            <Souhrn polozky={[
                { hodnota: String(zastavky.length), popis: zastavek(zastavky.length) },
                { hodnota: `${davka.kds_objednavek_hotovych ?? 0}/${davka.kds_objednavek_celkem ?? zastavky.length}`, popis: 'objednávek hotovo' },
                { hodnota: kc(penize.celkem), popis: 'vybrat' },
            ]} />
            <Akce disabled Icon={ChefHat} sipka={false}>Dám vědět, až bude hotovo</Akce>
        </Deska>
    );
}

function KartaNavrat({ davka, zastavky, onRefetch }) {
    const [busy, setBusy] = useState(false);
    const doruceno = zastavky.filter(z => z.stav_doruceni === 'doruceno').length;
    const pozde = zastavky.filter(z => String(z.vysledek_duvod || '').startsWith('pozdě')).length;
    const zpet = zastavky.filter(z => z.stav_doruceni === 'nezastizen');

    const hotovo = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const r = await fetch(`/api/rozvoz/davky/${davka.id}/cockpit-zavrit`, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            if (r.ok) onRefetch?.();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Deska>
            <Hlava barva={OK} stav="Jízda hotová" kod={davka.kod} />
            <Radek>
                <Stitek barva={OK}>{doruceno} doručeno</Stitek>
                {pozde > 0 && <Stitek barva={POZOR}>{pozde} pozdě</Stitek>}
                {zpet.length > 0 && <Stitek barva={CHYBA}>{zpet.length} zpět</Stitek>}
            </Radek>
            {zpet.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 14, background: tint(CHYBA, 0.1), border: `1.5px solid ${tint(CHYBA, 0.45)}` }}>
                    <Undo2 size={22} style={{ color: CHYBA, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                        <Nad barva={CHYBA}>Vrať do bistra</Nad>
                        {zpet.map(z => (
                            <div key={z.objednavka_id} style={{ fontSize: 15, fontWeight: 800 }}>
                                #{z.cislo_denni ?? z.cislo_objednavky} · {ulice(z.adresa)}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <Akce barva={OK} onClick={hotovo} disabled={busy} sipka={false} Icon={busy ? Loader2 : null}>Hotovo</Akce>
        </Deska>
    );
}

/** Další dávka čekající po návratu — jeden řádek, ať nesoupeří s aktuální. */
function DalsiDavka({ davka, zastavky, onOpenCockpit }) {
    return (
        <button type="button" onClick={onOpenCockpit} style={{
            display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, padding: '0 14px',
            borderRadius: 16, cursor: 'pointer', textAlign: 'left', font: 'inherit',
            background: 'var(--surface-raised)', border: '1.5px dashed var(--border-strong)', color: 'var(--text-primary)',
            WebkitTapHighlightColor: 'transparent',
        }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800 }}>
                Po návratu · {davka.kod} · {zastavky.length} {zastavek(zastavky.length)}
            </span>
            <ArrowRight size={18} style={{ color: AKCENT, flexShrink: 0 }} />
        </button>
    );
}

// ── Stavební prvky ──────────────────────────────────────────────────────

/**
 * Sklo jako plovoucí lišta a horní ovládání (`skloMaterial`) — karta leží
 * nad září pozadí a prosvítá jí, stejně jako lišta pod ní.
 */
function Deska({ children }) {
    const { isDark } = useTheme() ?? {};
    return (
        <section style={{
            ...skloMaterial(isDark !== false), border: '1px solid var(--border-default)', borderRadius: 20,
            padding: 16, display: 'flex', flexDirection: 'column', gap: 14, color: 'var(--text-primary)',
        }}>
            {children}
        </section>
    );
}

function Hlava({ barva, stav, kod, pulz = false }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {pulz && (
                <style>{`
                    @keyframes davka-pulz { 50% { box-shadow: 0 0 0 7px rgba(255,138,0,0); } }
                    @media (prefers-reduced-motion: no-preference) { .davka-pulz { animation: davka-pulz 2.4s ease-in-out infinite; } }
                `}</style>
            )}
            <span className={pulz ? 'davka-pulz' : undefined} style={{
                width: 10, height: 10, borderRadius: 99, flexShrink: 0, background: barva,
                boxShadow: pulz ? `0 0 0 4px ${tint(AKCENT, 0.16)}` : 'none',
            }} />
            <span style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: barva }}>{stav}</span>
            <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>{kod}</span>
        </div>
    );
}

/** Pruh průběhu — co segment, to zastávka. */
function Prubeh({ zastavky, idx }) {
    if (!zastavky.length) return null;
    return (
        <div style={{ display: 'flex', gap: 4 }} aria-label={`Zastávka ${Math.min(idx + 1, zastavky.length)} z ${zastavky.length}`}>
            {zastavky.map((z, i) => (
                <i key={z.objednavka_id} style={{
                    flex: 1, height: 6, borderRadius: 3,
                    background: z.stav_doruceni === 'doruceno' ? OK
                        : z.stav_doruceni === 'nezastizen' ? CHYBA
                            : i === idx ? AKCENT : 'var(--surface-elevated)',
                }} />
            ))}
        </div>
    );
}

function Nad({ children, barva }) {
    return (
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: barva ?? 'var(--text-muted)' }}>
            {children}
        </span>
    );
}

function Hlavni({ nad, velke, pod, mensi = false }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <Nad>{nad}</Nad>
            <span style={{ fontSize: mensi ? 18 : 22, fontWeight: 800, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{velke}</span>
            {pod && <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{pod}</span>}
        </div>
    );
}

function Radek({ children }) {
    return <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{children}</div>;
}

function Stitek({ barva, Icon, children }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 800,
            padding: '6px 10px', borderRadius: 9,
            background: barva ? tint(barva, 0.12) : 'var(--surface-elevated)',
            border: `1px solid ${barva ? tint(barva, 0.38) : 'var(--border-default)'}`,
            color: barva ?? 'var(--text-secondary)',
        }}>
            {Icon && <Icon size={15} />}{children}
        </span>
    );
}

function Souhrn({ polozky }) {
    return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${polozky.length}, minmax(0, 1fr))`, borderTop: '1px solid var(--border-default)', paddingTop: 12 }}>
            {polozky.map((p, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, ...(i ? { paddingLeft: 12, borderLeft: '1px solid var(--border-default)' } : {}) }}>
                    <b style={{ fontSize: 17, fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: p.barva ?? 'var(--text-primary)' }}>{p.hodnota}</b>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.popis}</span>
                </div>
            ))}
        </div>
    );
}

/** Jediná hlavní akce karty — plná, 56 px, přes celou šířku. */
function Akce({ children, barva, onClick, disabled, Icon, sipka = true }) {
    const plne = !!barva && !disabled;
    return (
        <button type="button" onClick={onClick} disabled={disabled} style={{
            minHeight: 56, borderRadius: 16, font: 'inherit', fontSize: 17, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            cursor: disabled ? 'default' : 'pointer', WebkitTapHighlightColor: 'transparent',
            ...(plne
                ? { background: barva, color: '#0A0A0C', border: `1px solid ${barva}` }
                : { background: 'var(--surface-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border-default)' }),
        }}>
            {Icon && <Icon size={19} className={Icon === Loader2 ? 'animate-spin' : undefined} />}
            {children}
            {sipka && plne && <ArrowRight size={19} strokeWidth={2.6} />}
        </button>
    );
}
